import { useState } from 'react';
import type { ReactElement } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { formatDate } from '@xdocs/i18n';
import type { CommentListRow, UserRole } from '@xdocs/schemas';
import { Avatar, Badge, Button, ConfirmDialog, EmptyState, Skeleton, Textarea, cn, useI18n, useToast } from '@xdocs/ui';
import { TakedownDialog } from './TakedownDialog.js';
import { useComments } from './useComments.js';
import { useSharingT } from './useSharingT.js';
import styles from './CommentThread.module.css';

export interface CommentThreadProps {
  shareId: string;
  /** `null` for an anonymous (logged-out) viewer — the composer stays
   * visible but disabled with a sign-in reason (never a dead control). */
  viewerUserId: string | null;
  /** `null` hides every moderator-only "Take down" entry point — see
   * `useViewerRole.ts`'s doc comment for why this is `null` today. */
  viewerRole: UserRole | null;
  className?: string | undefined;
}

/** F28 threaded comments — top-level thread + lazily-fetched reply pages,
 * PRO badge (F28), own-delete, moderator takedown (F69). */
export function CommentThread({ shareId, viewerUserId, viewerRole, className }: CommentThreadProps): ReactElement {
  const { t: tCommon } = useI18n();
  const { t } = useSharingT();
  const toast = useToast();
  const thread = useComments(shareId);
  const [draft, setDraft] = useState('');
  const [postError, setPostError] = useState<string | null>(null);
  const canComment = viewerUserId !== null;

  async function handlePost(): Promise<void> {
    if (!draft.trim()) return;
    setPostError(null);
    try {
      await thread.post(draft.trim());
      setDraft('');
    } catch (err) {
      const alert = err as { message?: string };
      setPostError(alert.message ?? t('sharing:comments.postFailed'));
      toast.push({ tone: 'error', message: t('sharing:comments.postFailed') });
    }
  }

  return (
    <section className={cn(styles['thread'], className)} aria-label={t('sharing:comments.title', { count: thread.total ?? thread.rows.length })}>
      <h2 className={cn(styles['title'])}>{t('sharing:comments.title', { count: thread.total ?? thread.rows.length })}</h2>

      <div className={cn(styles['composer'])}>
        <Avatar name={viewerUserId ?? '?'} size={34} />
        <div className={cn(styles['composerInput'])}>
          <Textarea
            label={t('sharing:comments.composer.placeholder')}
            placeholder={t('sharing:comments.composer.placeholder')}
            value={draft}
            disabled={!canComment}
            onChange={(event) => setDraft(event.target.value)}
          />
          {postError && (
            <p role="alert" className={cn(styles['inlineError'])}>
              {postError}
            </p>
          )}
          <Button
            variant="primary"
            state={thread.isPosting ? 'loading' : 'idle'}
            disabled={!canComment || draft.trim().length === 0}
            {...(!canComment ? { disabledReason: t('sharing:comments.composer.signInToComment') } : {})}
            onClick={() => void handlePost()}
          >
            {t('sharing:comments.composer.post')}
          </Button>
        </div>
      </div>

      {thread.isLoading && (
        <div aria-hidden="true" className={cn(styles['skeletonList'])}>
          <Skeleton shape="row" count={3} />
        </div>
      )}

      {!thread.isLoading && thread.isError && (
        <EmptyState kind="error" title={t('sharing:comments.error.title')} action={{ label: tCommon('common:action.retry'), onClick: thread.onRetry }} mascotAlt="" />
      )}

      {!thread.isLoading && !thread.isError && thread.rows.length === 0 && (
        <EmptyState kind="first-use" title={t('sharing:comments.empty.title')} description={t('sharing:comments.empty.description')} mascotAlt="" />
      )}

      {!thread.isLoading && !thread.isError && thread.rows.length > 0 && (
        <ul className={cn(styles['list'])}>
          {thread.rows.map((comment) => (
            <li key={comment.id}>
              <CommentNode shareId={shareId} comment={comment} viewerUserId={viewerUserId} viewerRole={viewerRole} onRemoved={thread.onRetry} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface CommentNodeProps {
  shareId: string;
  comment: CommentListRow;
  viewerUserId: string | null;
  viewerRole: UserRole | null;
  onRemoved: () => void;
}

function CommentNode({ shareId, comment, viewerUserId, viewerRole, onRemoved }: CommentNodeProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useSharingT();
  const toast = useToast();
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [takedownOpen, setTakedownOpen] = useState(false);
  const replies = useComments(shareId, comment.id, { enabled: showReplies });
  const canModerate = viewerRole === 'moderator' || viewerRole === 'admin';
  const isOwn = viewerUserId !== null && viewerUserId === comment.authorUserId;
  const isRemoved = comment.deletedAt !== null;

  async function handleReply(): Promise<void> {
    if (!replyDraft.trim()) return;
    await replies.post(replyDraft.trim(), comment.id);
    setReplyDraft('');
    setShowReplyForm(false);
    setShowReplies(true);
  }

  async function handleDelete(): Promise<void> {
    try {
      // `replies` (this node's OWN reply-page hook) exposes the same
      // `comment.remove` mutation `useComments` always provides regardless
      // of `parentCommentId`/`enabled` — reused here rather than mounting a
      // second hook instance just to delete THIS comment (not a reply).
      await replies.remove(comment.id);
      onRemoved();
    } catch {
      toast.push({ tone: 'error', message: t('sharing:comments.deleteFailed') });
    } finally {
      setDeleteConfirmOpen(false);
    }
  }

  return (
    <div className={cn(styles['comment'])}>
      <Avatar name={comment.authorDisplayName ?? '?'} size={34} />
      <div className={cn(styles['commentBody'])}>
        <div className={cn(styles['commentHeader'])}>
          <span className={cn(styles['author'])}>{comment.authorDisplayName ?? '—'}</span>
          {comment.authorIsPro && <Badge tone="pro">{t('sharing:comments.badge.pro')}</Badge>}
          <span className={cn(styles['time'])}>{formatDate(locale, comment.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>
        </div>

        {isRemoved ? (
          <p className={cn(styles['removedNotice'])}>{comment.removedByUserId ? t('sharing:comments.removedByModerator') : t('sharing:comments.deleted')}</p>
        ) : (
          <p className={cn(styles['body'])}>{comment.body}</p>
        )}

        {!isRemoved && (
          <div className={cn(styles['actions'])}>
            <Button variant="ghost" size="sm" onClick={() => setShowReplyForm((v) => !v)}>
              {t('sharing:comments.reply')}
            </Button>
            {(comment.likeCount > 0 || comment.dislikeCount > 0) && (
              <span className={cn(styles['staticReactions'])}>
                <span aria-label={t('sharing:comments.likeCount', { count: comment.likeCount })}>
                  <ThumbsUp size={12} aria-hidden="true" /> {comment.likeCount}
                </span>
                <span aria-label={t('sharing:comments.dislikeCount', { count: comment.dislikeCount })}>
                  <ThumbsDown size={12} aria-hidden="true" /> {comment.dislikeCount}
                </span>
              </span>
            )}
            {isOwn && (
              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmOpen(true)}>
                {t('sharing:comments.delete')}
              </Button>
            )}
            {canModerate && !isOwn && (
              <Button variant="ghost" size="sm" onClick={() => setTakedownOpen(true)}>
                {t('sharing:moderator.takedown')}
              </Button>
            )}
          </div>
        )}

        {showReplyForm && (
          <div className={cn(styles['replyForm'])}>
            <Textarea
              label={t('sharing:comments.replyPlaceholder')}
              placeholder={t('sharing:comments.replyPlaceholder')}
              value={replyDraft}
              onChange={(event) => setReplyDraft(event.target.value)}
            />
            <Button variant="primary" size="sm" state={replies.isPosting ? 'loading' : 'idle'} disabled={replyDraft.trim().length === 0} onClick={() => void handleReply()}>
              {t('sharing:comments.composer.post')}
            </Button>
          </div>
        )}

        {comment.replyCount > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setShowReplies((v) => !v)}>
            {showReplies ? t('sharing:comments.hideReplies') : t('sharing:comments.viewReplies', { count: comment.replyCount })}
          </Button>
        )}

        {showReplies && (
          <ul className={cn(styles['replies'])}>
            {replies.isLoading && (
              <li aria-hidden="true">
                <Skeleton shape="row" count={comment.replyCount} />
              </li>
            )}
            {!replies.isLoading &&
              replies.rows.map((reply) => (
                <li key={reply.id}>
                  <CommentNode shareId={shareId} comment={reply} viewerUserId={viewerUserId} viewerRole={viewerRole} onRemoved={onRemoved} />
                </li>
              ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        action={t('sharing:comments.delete')}
        assetName={comment.authorDisplayName ?? comment.id}
        destructive
        onConfirm={() => void handleDelete()}
      />

      {canModerate && (
        <TakedownDialog
          open={takedownOpen}
          onOpenChange={setTakedownOpen}
          shareId={shareId}
          targetType="comment"
          targetId={comment.id}
          assetName={comment.authorDisplayName ?? comment.id}
          onRemoved={onRemoved}
        />
      )}
    </div>
  );
}
